// The Report page — a printable metocean brief for one operation, one place,
// one window of hours.
//
// This is the console's science, packaged: the same fetchPointForecast, the
// same effectiveLimit and workability(), the same window briefing and the same
// chart language, laid out as a document a client can print (browser print →
// PDF), circulate internally, and hand to THEIR clients. It replaces the
// per-project static point-forecast PDFs of the old TDI SeaState — on demand,
// for any point.
//
// Nothing scientific is computed here. This module asks the existing modules
// the questions the console asks, then typesets the answers. Every figure in
// the document traces to forecast data or to the client's registry; the one
// set of quoted literals (the forecast-skill citation) lives in
// report/report.ts beside its source.

import { fetchPointForecast } from "../forecast/openmeteo.js";
import type { ModelSeries, PointForecast } from "../forecast/openmeteo.js";
import { dprWeather, DPR_SEA_NOTE, DPR_SOURCE_NOTE } from "../ops/dpr.js";
import {
  weatherDays, rainPhrase, tempPhrase, visPhrase, pressurePhrase, GENWEATHER_NOTE,
} from "../ops/genweather.js";
import { conditionGrid, gridOmissions } from "../report/conditiongrid.js";
import { siteMap, siteMapCaption, frameBounds } from "../report/sitemap.js";
import { fetchJurisdiction } from "../map/overlays.js";
import {
  loadReportPrefs,
  applyReportPrefs,
  repLimitLen,
  repDepth,
  unitsNote,
  UNITS_INVARIANT_NOTE,
  type ReportPrefs,
  type ReportUnits,
} from "../report/prefs.js";
import { svpProfilesSvg, eqLabel } from "../svp/profile.js";
import { DEFAULT_EQ } from "../forecast/soundspeed.js";
import { fetchSvp } from "../data/api.js";
import { fetchFieldMesh } from "../forecast/fieldmesh.js";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MO = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
import { solarSeries } from "../forecast/solar.js";
import type { Solar } from "../forecast/solar.js";
import { workGrade, workability } from "../ops/workability.js";
import type { HourlyWorkability } from "../ops/workability.js";
import { effectiveLimit } from "../ops/registry.js";
import type { EffectiveLimit, Operation, Registry, Vessel } from "../ops/registry.js";
import { buildBriefing } from "../ops/briefing.js";
import { createChart } from "../chart/chart.js";
import type { Chart, Mark, Series } from "../chart/chart.js";
import {
  cssVar,
  dirArrows,
  limitLine,
  nightBands,
  noGoSpans,
  nowLine,
  rgba,
} from "../chart/marks.js";
import { setLabelZone } from "../time/cursor.js";
import { getTimeMode } from "../ui/time.js";
import { parseCoords } from "../ui/coords.js";
import {
  fmtPosition,
  fmtPositionIn,
  getPositionFormat,
  positionCrs,
} from "../ui/position.js";
import { markSvg } from "../ui/mark.js";
import {
  convLen,
  convLenSeries,
  fmtDepth,
  fmtKm,
  fmtLimitLen,
  getUnitSystem,
  lenUnit,
  fmtLen,
} from "../ui/units.js";
import {
  confidenceStatement,
  durationChoices,
  fmtDM,
  fmtGenerated,
  fmtClock,
  clockNote,
  type ReportClock,
  reportFileName,
  reportRef,
  skillStatement,
  startInputValue,
  windowEnsWidth,
  windowIndices,
  windowModelSpread,
  windowsNear,
  worstHour,
} from "../report/report.js";

/**
 * A snapshot of the console's current state, taken by main.ts at the moment
 * the route is entered. The report's default question is "the thing I am
 * looking at in the console, as a document" — so the form opens on the picked
 * point, the armed vessel × operation, and the work window on the timeline.
 * Nulls fall back to sensible defaults (next 24 h, first vessel).
 */
export interface ReportPrefill {
  position: { lat: number; lon: number } | null;
  vesselId: number | null;
  opId: number | null;
  /** The timeline playhead — where the console's work window opens — or null
   *  when no window is set. */
  windowStartMs: number | null;
  /** The console work window's length in hours; null when no window is set. */
  windowHours: number | null;
}

export interface ReportOptions {
  registry: Registry;
  /** The signed-in client's organization name — "Prepared for {client}". */
  clientName: string;
  prefill: ReportPrefill;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

// Charts created by the current report, torn down on regenerate and on route
// change (main.ts calls unmountReport before replacing the page) so their
// cursor/theme subscriptions don't pile up behind detached canvases.
let liveCharts: Chart[] = [];

// --- entry modes -------------------------------------------------------------
//
// Two explicit doors into the same editable form. "From current view" fills
// every field from the console snapshot main.ts passed in — picked point,
// armed vessel × operation, the timeline's work window — and generates in one
// click. "Custom" is the blank manual form. Either way the fields stay
// editable; the mode note under the buttons says which door filled them, so a
// snapshot is never mistaken for defaults.

export function unmountReport(): void {
  for (const c of liveCharts) c.destroy();
  liveCharts = [];
}

/** Hours of forecast charted either side of the requested window — enough to
 *  see the approach and the exit without the window drowning in context. */
const CONTEXT_MARGIN_H = 12;

/** The plain-words reading printed beside each verdict label, so a grade is
 *  never colour-alone and never jargon-alone on a page that may be photocopied
 *  in grayscale. */
const GRADE_NOTES: Record<string, string> = {
  Go: "the operating limits hold through the window, including its worst hour",
  Marginal: "the worst hour keeps only a marginal share of model guidance inside the limits",
  Poor: "most model guidance breaches the operating limits at the worst hour",
  "No-go": "the operating limits are breached at the worst hour by nearly all model guidance",
};

/** The window's edges drawn on a chart: two accent bookends with a faint veil
 *  outside — the track stack's own grammar (bookends state the span; the
 *  outside stays legible), rebuilt index-based because the report's window is
 *  fixed at generation rather than driven by the shared timeline cursor. */
function windowBookends(i0: number, i1: number): Mark {
  return {
    layer: "front",
    draw({ ctx, xPx, area }) {
      const x0 = xPx(i0);
      const x1 = xPx(i1);
      ctx.save();
      ctx.fillStyle = cssVar("--surface", "#ffffff");
      ctx.globalAlpha = 0.45;
      if (x0 > area.left) ctx.fillRect(area.left, area.top, x0 - area.left, area.bottom - area.top);
      if (x1 < area.right) ctx.fillRect(x1, area.top, area.right - x1, area.bottom - area.top);
      ctx.restore();
      ctx.save();
      ctx.strokeStyle = cssVar("--accent", "#0f6b74");
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (const x of [x0, x1]) {
        if (x <= area.left || x >= area.right) continue;
        ctx.moveTo(Math.round(x) + 0.5, area.top);
        ctx.lineTo(Math.round(x) + 0.5, area.bottom);
      }
      ctx.stroke();
      ctx.restore();
    },
  };
}

const hasData = (m: ModelSeries): boolean => m.data.some((v) => v !== null);

const sliceSeries = (data: (number | null)[], ca: number, cb: number): (number | null)[] =>
  data.slice(ca, cb + 1);

interface GeneratedInputs {
  lat: number;
  lon: number;
  vessel: Vessel;
  op: Operation;
  limit: EffectiveLimit;
  startMs: number;
  hours: number;
}

export function mountReport(root: HTMLElement, opts: ReportOptions): void {
  unmountReport();
  const { registry, clientName } = opts;

  // ---- the form -------------------------------------------------------------

  const form = el("section", "block report-form");
  const bh = el("div", "bh");
  bh.append(
    el("span", "bt", "Operations forecast report"),
    el(
      "span",
      "bsub",
      "window · position · vessel · operation → a printable brief",
    ),
  );
  form.appendChild(bh);

  // The two doors (see the entry-modes note above).
  const entry = el("div", "rp-entry");
  const fromViewBtn = el("button", "btn primary", "From current view");
  fromViewBtn.type = "button";
  const customBtn = el("button", "btn", "Custom");
  customBtn.type = "button";
  const modeNote = el("span", "rp-modenote");
  entry.append(fromViewBtn, customBtn, modeNote);
  form.appendChild(entry);
  if (opts.prefill.position === null) {
    // Quietly unavailable, not an error on click: there is nothing to
    // snapshot until the console has a picked point.
    fromViewBtn.disabled = true;
    fromViewBtn.title = "No point picked in the console yet — click the map or type a position there first.";
  }

  const grid = el("div", "rp-formgrid");

  // Window start. The site's timezone is only known after the forecast is
  // fetched, so the typed time is read in the console's chosen clock — UTC
  // when the clock toggle says UTC, this device's clock otherwise — and the
  // generated document then prints every hour in both site time and UTC.
  const startFld = el("label", "fld");
  const startLab = el("span", "lab");
  startLab.textContent =
    getTimeMode() === "utc" ? "Window start (UTC)" : "Window start (this device's clock)";
  const startInput = document.createElement("input");
  startInput.type = "datetime-local";
  startInput.step = "3600";
  startFld.append(startLab, startInput);

  const durFld = el("label", "fld");
  durFld.appendChild(el("span", "lab", "Duration"));
  const durSel = document.createElement("select");
  durFld.appendChild(durSel);
  /** Rebuild the duration options: the presets, plus a carried non-preset
   *  length (the console's window, or a restored edit) inserted in order. */
  const rebuildDurations = (carried: number | null): void => {
    const keep = durSel.value;
    durSel.replaceChildren();
    for (const h of durationChoices(carried)) {
      const o = document.createElement("option");
      o.value = String(h);
      o.textContent = `${h} hours`;
      durSel.appendChild(o);
    }
    if (keep !== "" && [...durSel.options].some((o) => o.value === keep)) durSel.value = keep;
  };
  rebuildDurations(null);

  const posFld = el("label", "fld");
  posFld.appendChild(el("span", "lab", "Position"));
  const posInput = document.createElement("input");
  posInput.type = "text";
  posInput.spellcheck = false;
  posInput.placeholder = "27.5, -94.2 · 27°30'N 94°12'W";
  posFld.append(posInput);

  const vesselFld = el("label", "fld");
  vesselFld.appendChild(el("span", "lab", "Vessel"));
  const vSel = document.createElement("select");
  for (const v of registry.vessels) {
    const o = document.createElement("option");
    o.value = String(v.id);
    o.textContent = v.name;
    vSel.appendChild(o);
  }
  vesselFld.appendChild(vSel);

  const opFld = el("label", "fld");
  opFld.appendChild(el("span", "lab", "Operation"));
  const oSel = document.createElement("select");
  opFld.appendChild(oSel);

  grid.append(startFld, durFld, posFld, vesselFld, opFld);
  form.appendChild(grid);

  // The effective limit for the current pick, worded exactly as the console's
  // Workability panel words it — including the seeded-default warning, which
  // an external document must carry prominently rather than hide.
  const limitRow = el("div", "warnrow");
  form.appendChild(limitRow);

  // ---- what the document carries, and in whose conventions ------------------
  // Options, not settings. The console has one reader and can ask them; a report
  // is generated once and forwarded to somebody it will never meet, so these are
  // fixed at generation time and then NAMED on the face of the document. See
  // report/prefs.ts for why the units choice writes the console's own preference
  // instead of a private one.
  const stored = loadReportPrefs();
  const optRow = el("div", "rp-optrow");

  const selectFld = <T extends string>(
    label: string,
    choices: readonly (readonly [T, string])[],
    value: T,
  ): { fld: HTMLElement; sel: HTMLSelectElement } => {
    const fld = el("label", "fld rp-opt");
    fld.appendChild(el("span", "lab", label));
    const sel = document.createElement("select");
    for (const [v, t] of choices) {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = t;
      sel.appendChild(o);
    }
    sel.value = value;
    fld.appendChild(sel);
    return { fld, sel };
  };

  const clock = selectFld<ReportClock>(
    "Times",
    [
      ["both", "Site local + UTC"],
      ["site", "Site local only"],
      ["utc", "UTC only"],
    ],
    stored.clock,
  );
  const units = selectFld<ReportUnits>(
    "Heights & depths",
    [
      ["metric", "Metric — metres"],
      ["imperial", "Imperial — feet"],
      ["both-metric", "Both — metres first"],
      ["both-imperial", "Both — feet first"],
    ],
    stored.units,
  );

  const svpFld = el("label", "fld rp-opt rp-optcheck");
  const svpBox = document.createElement("input");
  svpBox.type = "checkbox";
  svpBox.checked = stored.svp;
  svpFld.append(svpBox, el("span", "lab", "Include sound-velocity profile"));

  optRow.append(clock.fld, units.fld, svpFld);
  form.appendChild(optRow);

  const readPrefs = (): ReportPrefs => ({
    clock: clock.sel.value as ReportClock,
    units: units.sel.value as ReportUnits,
    svp: svpBox.checked,
  });
  // Applied on change rather than only on Generate, so the effect is visible
  // where it is chosen: flipping to feet repaints the limit line below in feet,
  // in front of the operator, instead of surprising them in the document.
  const onOpt = (): void => {
    applyReportPrefs(readPrefs());
    paintLimit();
  };
  clock.sel.addEventListener("change", onOpt);
  units.sel.addEventListener("change", onOpt);
  svpBox.addEventListener("change", onOpt);

  const actions = el("div", "rp-actions");
  const genBtn = el("button", "btn primary", "Generate report");
  genBtn.type = "button";
  const printBtn = el("button", "btn", "Print / save as PDF");
  printBtn.type = "button";
  printBtn.disabled = true;
  printBtn.addEventListener("click", () => window.print());
  // Download: a self-contained .html — markup, stylesheet and the meteogram
  // canvases baked to PNG data URIs, one file, no server, no print dialog.
  // Deliberately NOT a generated PDF: a PDF library is a runtime dependency
  // (a decision, per conventions) costing ~100 KB+ against the bundle budget
  // to re-implement, worse, the pagination the browser's own print engine
  // already does with the print CSS below. The .html opens anywhere, mails
  // cleanly, and prints to PDF from the recipient's browser.
  const dlBtn = el("button", "btn", "Download .html");
  dlBtn.type = "button";
  dlBtn.disabled = true;
  dlBtn.addEventListener("click", () => {
    void downloadHtml();
  });
  const status = el("span", "rp-status");
  actions.append(genBtn, printBtn, dlBtn, status);
  form.appendChild(actions);

  const selVessel = (): Vessel | null =>
    registry.vessels.find((v) => v.id === Number(vSel.value)) ?? null;
  const selOp = (): Operation | null =>
    registry.operations.find((o) => o.id === Number(oSel.value)) ?? null;

  function fillOps(): void {
    const v = selVessel();
    oSel.replaceChildren();
    const ops = registry.operations.filter(
      (op) => v === null || v.operation_ids.includes(op.id),
    );
    for (const op of ops) {
      const o = document.createElement("option");
      o.value = String(op.id);
      o.textContent = op.label;
      oSel.appendChild(o);
    }
  }

  function paintLimit(): void {
    limitRow.textContent = "";
    const v = selVessel();
    const op = selOp();
    if (v === null || op === null) return;
    const lim = effectiveLimit(v, op, registry);
    if (lim === null) {
      limitRow.textContent =
        "This bespoke operation has no limits yet — set them in Vessels before reporting on it.";
      return;
    }
    limitRow.textContent = lim.isDefault
      ? `⚠ Using the seeded default limit (waves ≤ ${fmtLimitLen(lim.wave_m)} · wind ≤ ${lim.wind_kn} kn) — ` +
        `set your own in Vessels to clear this. The report will carry this notice.`
      : `Limits: waves ≤ ${fmtLimitLen(lim.wave_m)} · wind ≤ ${lim.wind_kn} kn · task ≥ ${lim.task_hours} h (yours)`;
  }

  // Which door filled the form, and whether it has been touched since. The
  // note keeps a snapshot from being mistaken for defaults — and an edited
  // snapshot from being mistaken for the console.
  let mode: "custom" | "view" = "custom";
  let editedSinceFill = false;
  const updateNote = (): void => {
    if (mode === "view") {
      modeNote.textContent = editedSinceFill
        ? "Started from the console's current view — edited since."
        : "Filled from the console's current view: its picked point, armed vessel × operation, and work window.";
    } else {
      modeNote.textContent = fromViewBtn.disabled
        ? "Custom entry. (From current view needs a point picked in the console first.)"
        : "Custom entry.";
    }
  };
  const touched = (): void => {
    if (mode === "view" && !editedSinceFill) {
      editedSinceFill = true;
      updateNote();
    }
  };

  vSel.addEventListener("change", () => {
    fillOps();
    paintLimit();
    touched();
  });
  oSel.addEventListener("change", () => {
    paintLimit();
    touched();
  });
  startInput.addEventListener("input", touched);
  durSel.addEventListener("change", touched);
  posInput.addEventListener("input", touched);

  /** Select a vessel and an operation by id, tolerating ids that no longer
   *  exist in the registry (a deleted vessel, a removed bespoke op) by
   *  leaving the browser's default selection. */
  const selectPair = (vesselId: string | null, opId: string | null): void => {
    if (vesselId !== null && registry.vessels.some((v) => String(v.id) === vesselId)) {
      vSel.value = vesselId;
    }
    fillOps();
    if (opId !== null && [...oSel.options].some((o) => o.value === opId)) {
      oSel.value = opId;
    }
  };

  /** The blank manual form: next full hour, 24 h, position typed by hand. */
  const setCustomDefaults = (): void => {
    startInput.value = startInputValue(Date.now() + 3_600_000, getTimeMode() === "utc");
    rebuildDurations(null);
    durSel.value = "24";
    posInput.value = "";
    fillOps();
    paintLimit();
  };

  fromViewBtn.addEventListener("click", () => {
    const pf = opts.prefill;
    if (pf.position === null) return; // disabled anyway; belt and braces
    mode = "view";
    editedSinceFill = false;
    startInput.value = startInputValue(
      pf.windowStartMs ?? Date.now() + 3_600_000,
      getTimeMode() === "utc",
    );
    const hours = pf.windowHours !== null ? Math.max(1, Math.round(pf.windowHours)) : 24;
    rebuildDurations(hours);
    durSel.value = String(hours);
    posInput.value = `${pf.position.lat.toFixed(4)}, ${pf.position.lon.toFixed(4)}`;
    selectPair(
      pf.vesselId !== null ? String(pf.vesselId) : null,
      pf.opId !== null ? String(pf.opId) : null,
    );
    paintLimit();
    updateNote();
    // One click from console state to document.
    void generate();
  });

  customBtn.addEventListener("click", () => {
    mode = "custom";
    editedSinceFill = false;
    setCustomDefaults();
    updateNote();
    posInput.focus();
  });

  setCustomDefaults();
  updateNote();

  // ---- the document mount ---------------------------------------------------

  const docHost = el("div", "rp-dochost");

  // What the last generation was about — names the downloaded file.
  let lastMeta: { ref: string; vesselName: string; opLabel: string } | null = null;

  genBtn.addEventListener("click", () => {
    void generate();
  });

  /**
   * Bake the rendered document into one self-contained .html file and hand it
   * to the browser as a download. The stylesheet is inlined verbatim (so the
   * print CSS travels too — the recipient's browser paginates it exactly as
   * ours does), and each meteogram canvas is replaced by a PNG data URI at
   * its full 2x backing resolution.
   */
  async function downloadHtml(): Promise<void> {
    const docEl = docHost.querySelector(".report-doc");
    if (docEl === null || lastMeta === null) return;
    let css = "";
    try {
      const res = await fetch("/style.css");
      if (res.ok) css = await res.text();
    } catch {
      // No stylesheet is a degraded file, not a failed one — the prose and
      // the baked chart images still carry the content.
    }
    const clone = docEl.cloneNode(true) as HTMLElement;
    const srcCanvases = docEl.querySelectorAll("canvas");
    const dstCanvases = clone.querySelectorAll("canvas");
    dstCanvases.forEach((c, i) => {
      const src = srcCanvases[i];
      if (src === undefined) return;
      const img = document.createElement("img");
      img.src = src.toDataURL("image/png");
      img.alt = "meteogram";
      img.className = "rp-chartimg";
      c.replaceWith(img);
    });
    // Charts were painted under the theme active at generation; carrying the
    // data-theme attribute keeps the standalone page's text in the same table.
    const themeAttr = document.documentElement.getAttribute("data-theme");
    const theme = themeAttr === "dusk" || themeAttr === "night" ? themeAttr : null;
    const html =
      "<!doctype html>\n" +
      `<html lang="en"${theme !== null ? ` data-theme="${theme}"` : ""}>\n<head>\n` +
      '<meta charset="utf-8">\n' +
      '<meta name="viewport" content="width=device-width, initial-scale=1">\n' +
      `<title>${lastMeta.ref} — SeaState operations forecast</title>\n` +
      `<style>\n${css}\n` +
      "/* standalone shell — the document is the page */\n" +
      "body { margin: 0; padding: 24px 16px; }\n" +
      ".report-doc { max-width: 850px; margin: 0 auto; }\n" +
      ".rp-chartimg { display: block; width: 100%; height: 100%; object-fit: contain; }\n" +
      ".rp-charts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }\n" +
      ".rp-mapimg { display: block; width: 100%; aspect-ratio: 8/5; }\n" +
      "@media (max-width: 620px) { .rp-charts { grid-template-columns: 1fr; } }\n" +
      "@media print { body { padding: 0; } .report-doc { max-width: none; } }\n" +
      "</style>\n</head>\n<body>\n" +
      clone.outerHTML +
      "\n</body>\n</html>\n";
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = reportFileName(lastMeta.ref, lastMeta.vesselName, lastMeta.opLabel);
    link.click();
    // Revoke later, not immediately — some engines read the URL after click.
    window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }

  async function generate(): Promise<void> {
    status.textContent = "";
    status.classList.remove("err");
    const pos = parseCoords(posInput.value);
    if (pos === null) {
      status.textContent = "Can't read that position — decimal, N/S E/W, or degrees-minutes.";
      status.classList.add("err");
      return;
    }
    const vessel = selVessel();
    const op = selOp();
    if (vessel === null || op === null) {
      status.textContent = "Pick a vessel and an operation.";
      status.classList.add("err");
      return;
    }
    const limit = effectiveLimit(vessel, op, registry);
    if (limit === null) {
      status.textContent =
        "This bespoke operation has no limits yet — set them in Vessels first.";
      status.classList.add("err");
      return;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(startInput.value);
    if (m === null) {
      status.textContent = "Pick a window start time.";
      status.classList.add("err");
      return;
    }
    const [y, mo, d, h, min] = [m[1], m[2], m[3], m[4], m[5]].map(Number);
    const startMs =
      getTimeMode() === "utc"
        ? Date.UTC(y, mo - 1, d, h, min)
        : new Date(y, mo - 1, d, h, min).getTime();
    const hours = Number(durSel.value);

    genBtn.disabled = true;
    status.textContent = "Fetching forecast…";
    try {
      const fc = await fetchPointForecast(pos.lat, pos.lon);
      status.textContent = "";
      renderDocument(
        fc,
        { lat: pos.lat, lon: pos.lon, vessel, op, limit, startMs, hours },
        readPrefs(),
      );
      printBtn.disabled = false;
      dlBtn.disabled = lastMeta === null;
    } catch {
      status.textContent = "No forecast data came back for that point — try again.";
      status.classList.add("err");
    } finally {
      genBtn.disabled = false;
    }
  }

  // ---- document rendering ---------------------------------------------------

  function renderDocument(
    fc: PointForecast,
    input: GeneratedInputs,
    prefs: ReportPrefs,
  ): void {
    unmountReport();
    docHost.replaceChildren();
    // The report speaks the console's stated time convention: labels parse and
    // format in the site's zone. This also aligns the briefing's prose.
    setLabelZone(fc.utcOffsetSeconds);

    const win = windowIndices(fc.times, fc.utcOffsetSeconds, input.startMs, input.hours, CONTEXT_MARGIN_H);
    if (win === null) {
      const miss = el("section", "block");
      miss.appendChild(
        el(
          "p",
          "note",
          "The requested window lies outside the forecast's coverage " +
            "(about 2 days back to 7 days ahead). Choose a start inside that range.",
        ),
      );
      docHost.appendChild(miss);
      printBtn.disabled = true;
      dlBtn.disabled = true;
      lastMeta = null;
      return;
    }

    const { a, b, ca, cb } = win;
    const solar = solarSeries(fc.times, fc.utcOffsetSeconds, fc.lat, fc.lon);
    const wk: HourlyWorkability = workability(fc, {
      wave_m: input.limit.wave_m,
      wind_kn: input.limit.wind_kn,
      squall: input.limit.squall,
      taskHours: input.limit.task_hours,
    });

    // Caveats are COLLECTED, not interleaved. What a reader needs in order to
    // READ a number stays beside it (which clock, which units); what QUALIFIES a
    // number — provenance, model spread, what it cannot tell you — goes to one
    // block at the back. Interleaved, the qualifications were the document: the
    // reader met a paragraph of hedging between every two facts, and the facts
    // stopped arriving. At the back they are still there, still honest, and no
    // longer in the way.
    const caveats: { head: string; body: string }[] = [];

    const now = new Date();
    const ref = reportRef(now);
    lastMeta = { ref, vesselName: input.vessel.name, opLabel: input.op.label };
    const at = (i: number): number =>
      Date.parse(fc.times[i] + ":00Z") - fc.utcOffsetSeconds * 1000;
    // Every hour printed anywhere in the document goes through here, which is
    // why the clock option is one line rather than a sweep: there is exactly one
    // place that turns an index into a time.
    const both = (i: number): string => fmtClock(at(i), fc.utcOffsetSeconds, prefs.clock);
    /** The calendar the labels are read in — the site's, or UTC when asked. */
    const labelDate = (i: number): Date =>
      prefs.clock === "utc" ? new Date(at(i)) : new Date(at(i) + fc.utcOffsetSeconds * 1000);

    const doc = el("article", "report-doc");

    // -- header ---------------------------------------------------------------
    const head = el("header", "rp-head");
    const brandrow = el("div", "rp-brandrow");
    const markBox = el("span", "rp-mark");
    markBox.innerHTML = markSvg(40);
    const titleBox = el("div", "rp-title");
    titleBox.append(
      el("div", "rp-word", "SEASTATE"),
      el("div", "rp-doctype", "Marine operations forecast"),
    );
    const refBox = el("div", "rp-refbox");
    refBox.append(
      el("div", "rp-ref", ref),
      el("div", undefined, `Generated ${fmtGenerated(now)}`),
      el("div", undefined, `Prepared for ${clientName}`),
    );
    brandrow.append(markBox, titleBox, refBox);
    head.appendChild(brandrow);

    // Particulars: the who/where/when block a reader scans first.
    const partics = el("dl", "rp-partics");
    const row = (k: string, v: string): void => {
      partics.append(el("dt", undefined, k), el("dd", undefined, v));
    };
    // Two spellings always — decimal for a machine, degrees-and-minutes for a
    // radio — plus UTM when the console is set to it, because that is the
    // spelling their Guyana reporting uses and re-deriving it by hand is where
    // transcription errors come from.
    const utmRow =
      getPositionFormat() === "utm" ? `  ·  ${fmtPositionIn(fc.lat, fc.lon, "utm")}` : "";
    row(
      "Position",
      `${Math.abs(fc.lat).toFixed(4)}°${fc.lat >= 0 ? "N" : "S"} ` +
        `${Math.abs(fc.lon).toFixed(4)}°${fc.lon >= 0 ? "E" : "W"}  ·  ${fmtDM(fc.lat, fc.lon)}${utmRow}`,
    );
    // THE DATUM, ON THE FACE OF THE DOCUMENT. A position without its reference
    // system is a number that looks like one, and this document goes to people
    // whose own reports give the CRS a numbered section before any science —
    // while one table in their corpus states station coordinates with no datum
    // at all (docs/DELIVERABLES.md). One line closes that.
    row("Reference system", positionCrs(fc.lat, fc.lon));
    // Site context from the forecast's own bathymetry — cheap, already fetched.
    if (fc.depthM !== null) {
      const relief = fc.relief;
      row(
        "Site",
        `water depth about ${repDepth(fc.depthM, prefs.units)}` +
          (relief !== null
            ? `; seabed ${repDepth(relief.shallow, prefs.units)}–${repDepth(relief.deep, prefs.units)} within ~${fmtKm(5)} (NCEI, coarse)`
            : ""),
      );
    } else if (fc.landElevM !== null) {
      row("Site", `charted as land (elevation ${repDepth(fc.landElevM, prefs.units)}) — check the position`);
    }
    const particulars = [
      input.vessel.loa_m !== null ? `LOA ${input.vessel.loa_m} m` : null,
      input.vessel.roll_period_s !== null ? `roll ≈${input.vessel.roll_period_s} s` : null,
      input.vessel.dp ? "DP" : null,
    ]
      .filter((s): s is string => s !== null)
      .join(" · ");
    row("Vessel", input.vessel.name + (particulars !== "" ? `  (${particulars})` : ""));
    row("Operation", input.op.label);
    row("Window", `${both(a)} → ${both(b)}  ·  ${b - a + 1} h`);
    // The conventions the rest of the document is read in, stated where a reader
    // meets them rather than discovered from a stray unit halfway down. A single
    // clock or a single system is a legitimate choice; leaving the reader to
    // work out WHICH one is not.
    row(
      "Conventions",
      `${clockNote(prefs.clock, fc.utcOffsetSeconds)} ${unitsNote(prefs.units)}`,
    );
    if (win.clampedStart || win.clampedEnd) {
      row(
        "Note",
        `the requested ${input.hours} h window overhangs the forecast's coverage and was ` +
          `clamped to the ${b - a + 1} h shown`,
      );
    }
    head.appendChild(partics);
    doc.appendChild(head);
    // What does NOT move with the units choice, said once at the back rather
    // than qualified beside every number. A reader who meets feet in one place
    // and knots in another is owed the reason.
    caveats.push({ head: "Units", body: UNITS_INVARIANT_NOTE });

    // -- verdict --------------------------------------------------------------
    const verdictSec = el("section", "rp-verdict");
    const worst = worstHour(wk.p, a, b);
    if (worst === null) {
      verdictSec.appendChild(
        el(
          "p",
          "note",
          "No workability verdict — the forecast carries no usable wind or wave data in this window.",
        ),
      );
    } else {
      const grade = workGrade(worst.p);
      const headRow = el("div", "rp-verdictrow");
      const chip = el("span", `rp-verdictchip ${grade.cls}`, grade.label);
      const line = el("span", "rp-verdictline");
      line.textContent =
        `${Math.round(worst.p * 100)}% of model guidance inside the operating limits at the ` +
        `window's worst hour — ${both(worst.index)}; ` +
        `${GRADE_NOTES[grade.label] ?? ""}.`;
      headRow.append(chip, line);
      verdictSec.appendChild(headRow);
      caveats.push({
        head: "How the verdict is graded",
        body:
          "The verdict describes the whole window, graded on its worst hour: a window that is " +
            "workable for all but one hour is not workable — the crew is on deck when it breaks." +
            (worst.covered < worst.total
              ? ` (${worst.total - worst.covered} of ${worst.total} hours carried no data and could not be graded.)`
              : ""),
      });
    }

    // Limits in force, and where they came from. A seeded default is a safety
    // fact this document must not soften: it goes in a bordered notice, not a
    // footnote.
    const limitsLine = el(
      "p",
      "rp-limits",
      `Operating limits applied: waves ≤ ${repLimitLen(input.limit.wave_m, prefs.units)} · ` +
        `wind ≤ ${input.limit.wind_kn} kn · task length ≥ ${input.limit.task_hours} h · ` +
        `squall sensitivity ${input.limit.squall}.`,
    );
    verdictSec.appendChild(limitsLine);
    if (input.limit.isDefault) {
      verdictSec.appendChild(
        el(
          "p",
          "rp-provisional",
          "PROVISIONAL LIMITS — the limits above are seeded industry defaults for this operation " +
            `class, scaled to the hull. They are not ${clientName}'s own certified numbers. ` +
            "Set vessel-specific limits in the console's Vessels page to clear this notice.",
        ),
      );
    } else {
      limitsLine.textContent += ` These are ${clientName}'s own limits for this vessel and operation.`;
    }

    // Weather windows meeting the task length, in and near the requested span.
    const near = windowsNear(wk.windows, a, b, 24);
    const wwHead = el("h3", "rp-h3", `Weather windows (≥ ${input.limit.task_hours} h workable)`);
    verdictSec.appendChild(wwHead);
    if (near.length === 0) {
      verdictSec.appendChild(
        el(
          "p",
          "note",
          wk.windows.length === 0
            ? `No ${input.limit.task_hours} h workable window exists anywhere in the 7-day forecast under these limits.`
            : `No workable window overlaps or adjoins the requested span; the nearest lies further out in the 7-day forecast.`,
        ),
      );
    } else {
      const list = el("div", "windows");
      for (const w of near) {
        const relation =
          w.relation === "overlaps"
            ? "overlaps the window"
            : w.relation === "before"
              ? "closes before the window"
              : "opens after the window";
        list.appendChild(
          el(
            "span",
            w.relation === "overlaps" ? "chip ok" : "chip",
            `${both(w.start)} → ${both(Math.max(w.start, w.end - 1))} · ${w.end - w.start} h — ${relation}`,
          ),
        );
      }
      verdictSec.appendChild(list);
    }
    doc.appendChild(verdictSec);

    // ---- the site map -------------------------------------------------------
    // Placed under the verdict, because it is the picture OF the verdict: the
    // field is coloured by the same limits the verdict was graded against.
    // Rendered asynchronously and inserted in place, so a slow basemap never
    // holds up the rest of the document.
    const mapSec = el("section", "rp-dpr rp-mapsec");
    mapSec.appendChild(el("div", "rp-dprhead", "Site map"));

    // Which maritime jurisdiction the site sits in. The console has had this
    // point-identify since v1 and the report never carried it — yet on a survey
    // it is the fact that decides whose permit, whose notice period and whose
    // reporting régime applies. The plate's graticule says where the point is;
    // this says whose water it is.
    //
    // As TEXT, not baked into the PNG: it must stay selectable, searchable and
    // copyable out of the document.
    const jurSlot = el("p", "rp-verdictnote");
    mapSec.appendChild(jurSlot);
    void (async (): Promise<void> => {
      const j = await fetchJurisdiction(fc.lat, fc.lon);
      if (j === null) {
        // Distinct from "high seas", which is a successful answer.
        jurSlot.textContent = "Maritime jurisdiction could not be looked up for this point.";
        return;
      }
      const bits: string[] = [];
      if (j.polType === "none") {
        bits.push("Jurisdiction: high seas — beyond any national EEZ.");
      } else {
        const who =
          j.sovereign !== null && !j.name.includes(j.sovereign) ? ` (${j.sovereign})` : "";
        bits.push(`Jurisdiction: ${j.name}${who}.`);
        if (j.disputed) {
          bits.push(
            `Classified "${j.polType}" — an overlapping claim or joint régime, not a plain ` +
              `200 nm EEZ. Confirm the permitting authority before mobilising.`,
          );
        }
      }
      // The disclaimer is not boilerplate here. A boundary dataset is a record of
      // claims; it does not decide who issues the permit, and a survey company
      // reading otherwise off our report is exactly the failure to prevent.
      bits.push(
        "Boundaries from Marine Regions (VLIZ) — informational, not a determination of permitting authority.",
      );
      jurSlot.textContent = bits.join(" ");
    })();

    const mapSlot = el("div", "rp-mapslot");
    mapSec.appendChild(mapSlot);
    doc.appendChild(mapSec);
    void (async (): Promise<void> => {
      const SPAN = 2;
      // The mesh box comes from the PLATE's own geometry, widened a little. The
      // frame is landscape and its width depends on latitude, so a square box
      // guessed here would leave the grid's edge showing inside the picture.
      const b = frameBounds(fc.lat, fc.lon, SPAN);
      const padLat = (b.latMax - b.latMin) * 0.12;
      const padLon = (b.lonMax - b.lonMin) * 0.12;
      const got = await fetchFieldMesh({
        latMin: b.latMin - padLat, latMax: b.latMax + padLat,
        lonMin: b.lonMin - padLon, lonMax: b.lonMax + padLon,
      });
      if (!got.ok) {
        // A named absence, not an empty frame: the reader must not read "no
        // field cover here" as "flat calm".
        mapSlot.appendChild(
          el("p", "rp-verdictnote",
            got.reason === "none"
              ? "No field grid covers this point, so no site map is drawn."
              : "The field grid could not be loaded, so no site map is drawn."),
        );
        return;
      }
      const worstIdx = worst === null ? a : worst.index;
      const wi = worstIdx;
      const windModel = fc.wind?.models.find((m) => m.data[wi] != null) ?? null;
      const waveModel = fc.waves?.height.find((m) => m.data[wi] != null) ?? null;
      const o = {
        lat: fc.lat, lon: fc.lon, mesh: got.mesh, instant: at(wi),
        layer: "limits" as const,
        limits: { windKn: input.limit.wind_kn, waveM: input.limit.wave_m },
        spanDeg: SPAN,
        // Stamped into the PNG so it still says where and when once it has been
        // lifted out of this document, which is what happens to a good figure.
        // Position, datum, conditions — in that order, so a figure lifted out
        // of this document still says where it is AND what that "where" means.
        stamp: [fmtPosition(fc.lat, fc.lon), positionCrs(fc.lat, fc.lon), both(wi)],
        wind: {
          dirDeg: fc.wind?.directionDeg[wi] ?? null,
          frac: windModel?.data[wi] == null ? null : (windModel.data[wi] as number) / input.limit.wind_kn,
          label: "Wind",
          // Knots regardless of the unit preference — wind at sea is knots, and
          // the console does not offer anything else. Wave height does convert,
          // so it goes through fmtLen and follows the operator's setting.
          text: windModel?.data[wi] == null
            ? undefined
            : `${Math.round(windModel.data[wi] as number)} kn`,
        },
        sea: {
          dirDeg: fc.waves?.directionDeg[wi] ?? null,
          frac: waveModel?.data[wi] == null ? null : (waveModel.data[wi] as number) / input.limit.wave_m,
          label: "Sea",
          text: waveModel?.data[wi] == null ? undefined : fmtLen(waveModel.data[wi] as number),
        },
      };
      const png = await siteMap(o);
      if (png === null) {
        mapSlot.appendChild(
          el("p", "rp-verdictnote", "The basemap could not be reached, so no site map is drawn."),
        );
        return;
      }
      const img = document.createElement("img");
      img.src = png;
      img.className = "rp-mapimg";
      img.alt = `Site map at ${fmtDM(fc.lat, fc.lon)}`;
      mapSlot.replaceChildren(img);
      mapSec.appendChild(el("p", "rp-verdictnote", `${both(wi)}. ${siteMapCaption(o)}`));
    })();

    // ---- the sound-velocity profile ------------------------------------------
    // Off unless asked for (report/prefs.ts gives the reason). Drawn by the
    // console's own svpProfilesSvg — the SAME figure the SVP workspace shows,
    // not a paper copy of it, so the screen and the document cannot come to
    // disagree about where the thermocline is.
    //
    // METRIC THROUGHOUT whatever the units option says: these depths pair with
    // m/s sound speeds and with the SI file the survey software ingests. A
    // profile in feet beside a sound speed in metres per second is a trap.
    if (prefs.svp) {
      const svpSec = el("section", "rp-dpr rp-svpsec");
      svpSec.appendChild(el("div", "rp-dprhead", "Sound-velocity profile — modelled"));
      const svpSlot = el("div", "rp-svpslot");
      svpSec.appendChild(svpSlot);
      doc.appendChild(svpSec);
      // Pushed HERE, not inside the fetch below: the notes section is built
      // synchronously further down, so a caveat added from the callback would
      // arrive after the block that carries it had already been written.
      caveats.push({
        head: "Reading the sound-velocity profile",
        body:
          "A modelled profile is a planning aid, not a cast. It is the CMEMS " +
          "analysis at the nearest grid cell, and the on-site SV cast remains the " +
          "survey record — the two will differ. Sound speed is recomputed from the " +
          "cell's temperature and salinity so every curve on the plot shares one " +
          "equation; gaps between the curves are the sea, not the arithmetic.",
      });
      void (async (): Promise<void> => {
        const got = await fetchSvp(fc.lat, fc.lon);
        // Three absences, three causes, worded apart — the same discipline the
        // site map follows. "No cell here" is not "the server refused".
        if (!got.ok) {
          svpSlot.appendChild(
            el("p", "rp-verdictnote", "The sound-velocity profile could not be loaded."),
          );
          return;
        }
        if (got.data === null) {
          svpSlot.appendChild(
            el("p", "rp-verdictnote",
              "No sound-velocity cell covers this point — the SVP grid is pre-fetched by region."),
          );
          return;
        }
        const svp = got.data;
        const c = svp.cell;
        const svg = svpProfilesSvg(c, DEFAULT_EQ, null, svp.climatology, fc.depthM, null);
        if (svg === "") {
          svpSlot.appendChild(
            el("p", "rp-verdictnote", "The profile at this cell has too few levels to plot."),
          );
          return;
        }
        const fig = el("div", "rp-svpfig");
        fig.innerHTML = svg; // our own generated markup, from numbers
        svpSlot.appendChild(fig);

        const facts = el("dl", "rp-partics rp-svpfacts");
        const f = (k: string, v: string): void => {
          facts.append(el("dt", undefined, k), el("dd", undefined, v));
        };
        f(
          "Mixed layer",
          c.mld === null ? "none resolved — stratified from the surface" : `${Math.round(c.mld)} m`,
        );
        f(
          "Thermocline",
          c.th === null
            ? "well mixed — none marked"
            : `${Math.round(c.th[0])}–${Math.round(c.th[1])} m · ${c.th[2].toFixed(1)} °C drop`,
        );
        f("Sound-channel axis", `${Math.round(c.ca).toLocaleString()} m`);
        f("Surface sound speed", `${c.cs.toFixed(1)} m/s`);
        // Three states, three sentences. `null` means the column ran to the
        // product's own floor — "at least this deep", which is knowledge.
        // ABSENT means this grid predates the field and knows nothing. Printing
        // the floor message for an absent field claims a 5,728 m seabed over a
        // 900 m site, which is how a false depth reaches a client.
        f(
          "Model seabed",
          c.sb === undefined
            ? "not recorded in this grid"
            : c.sb === null
              ? "below the model's deepest level (5,728 m)"
              : `${Math.round(c.sb).toLocaleString()} m`,
        );
        svpSlot.appendChild(facts);

        svpSlot.appendChild(
          el("p", "rp-verdictnote",
            `${svp.source}, ${svp.date}. Sound speed by ${eqLabel(DEFAULT_EQ)}.` +
              (svp.climatology === null
                ? ""
                : ` The muted curve is the WOA23 ${svp.climatology.monthName} average — the usual, beside the forecast.`) +
              " Metric throughout: these depths pair with m/s."),
        );
      })();
    }

    // -- meteograms -----------------------------------------------------------
    const chartsSec = el("section", "rp-charts");
    const labels = fc.times.slice(ca, cb + 1);
    const nightLevels = solar.level.slice(ca, cb + 1);
    const nowIdxLocal = fc.nowIndex - ca; // may be off-range; marks handle that
    const chartTitle = (i: number): string => both(i + ca);

    const addChart = (
      title: string,
      sub: string,
      build: (canvas: HTMLCanvasElement) => Chart | null,
      source: string,
    ): void => {
      const blockEl = el("section", "block rp-chartblock");
      const bhc = el("div", "bh");
      bhc.append(el("span", "bt", title), el("span", "bsub", sub));
      blockEl.appendChild(bhc);
      const wrap = el("div", "rp-chartwrap");
      const canvas = document.createElement("canvas");
      wrap.appendChild(canvas);
      blockEl.appendChild(wrap);
      blockEl.appendChild(el("div", "bsrc source", source));
      chartsSec.appendChild(blockEl);
      const chart = build(canvas);
      if (chart !== null) liveCharts.push(chart);
      else wrap.replaceChildren(el("p", "note", "Unavailable at this point."));
    };

    // Wind — the console's wind track, sliced to the report span.
    addChart(
      "Wind",
      "kn · 10 m · window bookended",
      (canvas) => {
        const w = fc.wind;
        if (w === null) return null;
        const m4 = cssVar("--m4", "#7b5ea7");
        const series: Series[] = [];
        if (w.ensemble !== null) {
          series.push({ label: "", data: sliceSeries(w.ensemble.p05, ca, cb), color: "transparent", width: 0, noLegend: true, noTooltip: true });
          series.push({ label: "", data: sliceSeries(w.ensemble.p95, ca, cb), color: "transparent", width: 0, fill: "-1", fillColor: rgba(m4, 0.14), noLegend: true, noTooltip: true });
          series.push({ label: "GEFS median", data: sliceSeries(w.ensemble.p50, ca, cb), color: m4, dashed: true });
        }
        for (const mm of w.models) {
          if (hasData(mm)) {
            series.push({ label: mm.label, data: sliceSeries(mm.data, ca, cb), color: cssVar(mm.slot, "#888") });
          }
        }
        const noGoSource =
          w.ensemble !== null
            ? sliceSeries(w.ensemble.p50, ca, cb)
            : (w.models.find(hasData) ? sliceSeries((w.models.find(hasData) as ModelSeries).data, ca, cb) : null);
        let peak = 0;
        for (const s of series) {
          for (const v of s.data) if (v != null && Number.isFinite(v) && v > peak) peak = v;
        }
        const limitKn = input.limit.wind_kn;
        const yMax = Math.max(Math.ceil(Math.max(peak * 1.1, limitKn * 1.05) / 5) * 5, 5);
        return createChart(canvas, {
          series,
          xLabels: labels,
          yUnit: "kn",
          yMin: 0,
          yMax,
          minDpr: 2,
          // Explicit, because the console's tracks now say `legend: false`. A
          // printed chart has no readout strip and no cursor to drive one, so
          // the in-canvas row is the ONLY thing naming these lines — the same
          // reason minDpr is pinned here rather than left to the screen.
          legend: true,
          tooltipTitle: chartTitle,
          marks: [
            nightBands(nightLevels),
            ...(noGoSource !== null ? [noGoSpans(noGoSource, limitKn, { unknownIsNoGo: false })] : []),
            limitLine(limitKn, { label: `limit ${limitKn} kn` }),
            nowLine(nowIdxLocal),
            dirArrows(sliceSeries(w.directionDeg, ca, cb)),
            windowBookends(a - ca, b - ca),
          ],
        });
      },
      "Source: GFS blend, ECMWF IFS, DWD ICON via Open-Meteo" +
        (fc.wind?.ensemble != null
          ? " · GEFS ensemble — dashed line is the member median, shaded band the 5–95% member spread"
          : "") +
        " · arrows: where the wind is going · shaded verticals: night",
    );

    // Waves — heights per model, display units, limit refline.
    addChart(
      "Waves",
      `significant height · ${lenUnit()} · window bookended`,
      (canvas) => {
        const w = fc.waves;
        if (w === null) return null;
        const series: Series[] = [];
        for (const mm of w.height) {
          if (hasData(mm)) {
            series.push({
              label: mm.label,
              data: convLenSeries(sliceSeries(mm.data, ca, cb)),
              color: cssVar(mm.slot, "#888"),
            });
          }
        }
        if (series.length === 0) return null;
        const first = w.height.find(hasData);
        const noGoSource = first ? convLenSeries(sliceSeries(first.data, ca, cb)) : null;
        const limitDisp = convLen(input.limit.wave_m);
        let peak = 0;
        for (const s of series) {
          for (const v of s.data) if (v != null && Number.isFinite(v) && v > peak) peak = v;
        }
        const yMax = Math.max(peak * 1.1, limitDisp * 1.05, convLen(1));
        return createChart(canvas, {
          series,
          xLabels: labels,
          yUnit: lenUnit(),
          yMin: 0,
          yMax: Math.ceil(yMax * 2) / 2,
          minDpr: 2,
          legend: true, // on paper the legend is the only thing naming the lines

          tooltipTitle: chartTitle,
          marks: [
            nightBands(nightLevels),
            ...(noGoSource !== null ? [noGoSpans(noGoSource, limitDisp, { unknownIsNoGo: false })] : []),
            limitLine(limitDisp, { label: `limit ${fmtLimitLen(input.limit.wave_m)}` }),
            nowLine(nowIdxLocal),
            dirArrows(sliceSeries(w.directionDeg, ca, cb)),
            windowBookends(a - ca, b - ca),
          ],
        });
      },
      "Source: GFS-Waves (WW3), ECMWF WAM, MFWAM, DWD GWAM via Open-Meteo Marine" +
        " · arrows: where the waves are running · shaded verticals: night",
    );
    doc.appendChild(chartsSec);

    // ---- the conditions grid ------------------------------------------------
    // The console's best instrument, on paper. Same rows, same numbers, same
    // grade colours — buildTabs and heat.ts, not a second implementation, so the
    // page and the screen cannot come to disagree in the client's hands.
    const gridCols: number[] = [];
    for (let i = ca; i <= cb; i += 3) gridCols.push(i);
    const gridInput = {
      fc, solar, columns: gridCols,
      vessel: input.vessel, limit: input.limit,
      opLabel: input.op.label, workP: wk.p,
    };
    const two = (n: number): string => String(n).padStart(2, "0");
    const grid = conditionGrid(gridInput, (i) => two(labelDate(i).getUTCHours()));
    if (grid !== null) {
      const gridSec = el("section", "rp-dpr rp-gridsec");
      gridSec.appendChild(el("div", "rp-dprhead", "Conditions — every three hours"));
      // Days are named once above the hour columns rather than repeated in every
      // heading; the grid is read across, and a repeated date is noise.
      const dayRow = el("div", "rp-griddays");
      let seen = "";
      for (const i of gridCols) {
        const label = (fc.times[i] ?? "").slice(0, 10);
        if (label !== seen) {
          seen = label;
          const d = labelDate(i);
          dayRow.appendChild(
            el("span", "rp-gridday", `${WD[d.getUTCDay()]} ${d.getUTCDate()} ${MO[d.getUTCMonth()]}`),
          );
        }
      }
      gridSec.appendChild(dayRow);
      gridSec.appendChild(grid);
      const dropped = gridOmissions(gridInput);
      gridSec.appendChild(
        el("p", "rp-verdictnote", clockNote(prefs.clock, fc.utcOffsetSeconds)),
      );
      caveats.push({
        head: "Reading the conditions grid",
        body:
          "Cells are tinted by how much of the operating limit the number uses; an " +
          "untinted number is not graded against a limit." +
          (dropped.length > 0 ? ` Not shown here: ${dropped.join(", ")}.` : ""),
      });
      doc.appendChild(gridSec);
    }

    // -- briefing prose -------------------------------------------------------
    const briefSec = el("section", "rp-briefsec");
    briefSec.appendChild(el("h3", "rp-h3", "Conditions briefing"));
    const brief = el("div", "brief");
    // The console's own deterministic window briefing, verbatim: every number
    // in it is computed from the forecast, which is exactly the property a
    // formal document needs. startHours is relative to the forecast's current
    // hour, the same offset the console passes.
    brief.innerHTML = buildBriefing({
      fc,
      solar,
      vessel: input.vessel,
      startHours: a - fc.nowIndex,
      lengthHours: b - a + 1,
    });
    briefSec.appendChild(brief);

    briefSec.appendChild(el("h3", "rp-h3", "Confidence & forecast horizon"));
    const isImp = getUnitSystem() === "imperial";
    const waveSpreadM = windowModelSpread(
      fc.waves !== null ? fc.waves.height.map((mm) => mm.data) : [],
      a,
      b,
    );
    briefSec.appendChild(
      el(
        "p",
        "rp-formal",
        confidenceStatement(
          windowEnsWidth(fc.ensembleMembers, a, b),
          waveSpreadM !== null ? convLen(waveSpreadM) : null,
          lenUnit(),
          // The briefing's ±0.25 / ±0.75 m thresholds, in the display unit.
          convLen(0.25),
          convLen(0.75),
        ),
      ),
    );
    briefSec.appendChild(
      el("p", "rp-formal", skillStatement(a - fc.nowIndex, b - fc.nowIndex)),
    );
    if (isImp) {
      briefSec.appendChild(
        el(
          "p",
          "rp-formal quiet",
          "Heights and depths are displayed in feet; limits are stored and applied in metres.",
        ),
      );
    }
    const briefSectionHold = briefSec;

    // ---- the Daily Progress Report line ------------------------------------
    // The one block in this document that is not ours: it is TDI-Brooks' own
    // form (docs/DELIVERABLES.md §2.0), reproduced in its order, vocabulary and
    // units so a party chief can read it straight across. BOEM NTL 2022-G01
    // §IV.B.3 requires "sea state and weather conditions" in the survey report,
    // and today that requirement is met by looking out of a window at 0700.
    const dprRows = dprWeather(fc).filter((w) => w.index >= ca && w.index <= cb);
    if (dprRows.length > 0) {
      const dprSec = el("section", "rp-dpr");
      dprSec.appendChild(el("div", "rp-dprhead", "Daily Progress Report — 0700 deck observation, one row per day"));
      dprSec.appendChild(
        el(
          "p",
          "rp-verdictnote",
          `Rows are 0700 on the site's clock — ${both(dprRows[0].index)}.`,
        ),
      );
      const tbl = el("table", "rp-dprtbl");
      const thead = el("thead");
      const hr = el("tr");
      for (const h of ["Date (local)", "Wind (dir/kts)", "Sea (type)", "Sea (dir/ft)", "Air", "Visibility", "Sky"]) {
        hr.appendChild(el("th", undefined, h));
      }
      thead.appendChild(hr);
      tbl.appendChild(thead);
      const tb = el("tbody");
      for (const w of dprRows) {
        const tr = el("tr");
        tr.appendChild(el("td", undefined, w.date));
        // An empty cell is left EMPTY, not filled with a dash or a guess — the
        // form has blanks, and their own example leaves Air blank.
        for (const v of [w.wind, w.seaType, w.sea, w.air, w.visibility, w.sky]) {
          tr.appendChild(el("td", undefined, v ?? ""));
        }
        tb.appendChild(tr);
      }
      tbl.appendChild(tb);
      dprSec.appendChild(tbl);
      caveats.push({ head: "The DPR weather line", body: `${DPR_SEA_NOTE} ${DPR_SOURCE_NOTE}` });
      doc.appendChild(dprSec);
    }

    // ---- general weather ---------------------------------------------------
    // A console can justify showing only what gates the task. A REPORT is
    // forwarded and filed, read by someone who was not at the screen, and a
    // marine forecast silent on rain, temperature and thunder reads as
    // incomplete to anyone who has stood on a deck. Deliberately conditions and
    // thresholds only — the synoptic discussion is a forecaster's product, and
    // this document does not compete with it.
    const wxDays = weatherDays(fc, ca, cb);
    if (wxDays.length > 0) {
      const wxSec = el("section", "rp-dpr");
      wxSec.appendChild(el("div", "rp-dprhead", "General weather — whole-day summary"));
      const wtbl = el("table", "rp-dprtbl");
      const wh = el("tr");
      for (const h of ["Date (local)", "Pressure · tendency", "Air", "Cloud", "Rain", "Thunder hrs", "Vis (low)"]) {
        wh.appendChild(el("th", undefined, h));
      }
      const wthead = el("thead");
      wthead.appendChild(wh);
      wtbl.appendChild(wthead);
      const wtb = el("tbody");
      for (const d of wxDays) {
        const tr = el("tr");
        tr.appendChild(el("td", undefined, d.date));
        tr.appendChild(el("td", undefined, pressurePhrase(d)));
        tr.appendChild(el("td", undefined, tempPhrase(d)));
        tr.appendChild(el("td", undefined, d.cloudPct === null ? "" : `${d.cloudPct}%`));
        tr.appendChild(el("td", undefined, rainPhrase(d)));
        // Null (the diagnostic did not run) prints blank; zero prints "0" — a
        // quiet day and an absent diagnostic are different answers.
        tr.appendChild(el("td", undefined, d.squallHours === null ? "" : String(d.squallHours)));
        tr.appendChild(el("td", undefined, visPhrase(d)));
        wtb.appendChild(tr);
      }
      wtbl.appendChild(wtb);
      wxSec.appendChild(wtbl);
      caveats.push({ head: "General weather", body: GENWEATHER_NOTE });
      doc.appendChild(wxSec);
    }

    // -- footer ---------------------------------------------------------------
    const foot = el("footer", "rp-foot");
    foot.appendChild(
      el(
        "p",
        "rp-sources",
        "Data: wind — GFS blend, ECMWF IFS, DWD ICON (Open-Meteo); waves — GFS-Waves (WW3), " +
          "ECMWF WAM, MFWAM, DWD GWAM (Open-Meteo Marine); wind ensemble — GEFS gfs025 " +
          "(Open-Meteo ensemble API); squall diagnostics — GFS convective fields (Open-Meteo); " +
          "currents & swell partition — best-match marine / Météo-France SMOC (Open-Meteo Marine); " +
          "depth & relief — NOAA NCEI global DEM; daylight — NOAA solar geometry, computed. " +
          `Forecast retrieved ${fmtGenerated(now)}; each model is its provider's latest ` +
          "available cycle as served by Open-Meteo.",
      ),
    );
    foot.appendChild(
      el(
        "p",
        "rp-notnav",
        "NOT FOR NAVIGATION. Decision support only: verdicts summarise forecast numbers and " +
          "inherit every limitation of their models. The master and the operator's own " +
          "procedures make the final call.",
      ),
    );
    // The reasoning, after the figures it reasons about.
    doc.appendChild(briefSectionHold);

    // ---- notes & limitations ------------------------------------------------
    // Everything that qualifies rather than states, in one place at the back.
    // Not hidden — a reader who wants the limits finds them together instead of
    // hunting them out of five separate paragraphs — but no longer standing
    // between the reader and the next number.
    if (caveats.length > 0) {
      const noteSec = el("section", "rp-notes");
      noteSec.appendChild(el("div", "rp-dprhead", "Notes & limitations"));
      const dl = el("dl", "rp-notelist");
      for (const c of caveats) {
        dl.appendChild(el("dt", undefined, c.head));
        dl.appendChild(el("dd", undefined, c.body));
      }
      noteSec.appendChild(dl);
      doc.appendChild(noteSec);
    }

    doc.appendChild(foot);

    // Repeating print footer (position:fixed repeats per printed page).
    const runfoot = el("div", "rp-runfoot");
    runfoot.textContent = `${ref} · SeaState operations forecast · prepared for ${clientName} · not for navigation`;
    doc.appendChild(runfoot);

    docHost.appendChild(doc);
  }

  root.append(form, docHost);
}
